import {useCallback, useContext, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useImageUpload} from '~/common/FabricInvitation/EventCreateDesign/components/PhotoEditor/common/useImageUpload';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {useUyo} from '~/common/fabric/Card/hooks/useUyo';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectCountOfUyoImages,
  selectFabricArtBackgroundImage,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {ImageFilters} from '../../actions/UyoControl/ImageFilters/ImageFilters';
import {MENU_TYPES, TRANSFORM_TYPES} from './contants';
import {MainPhotoEditorMenu} from './MainPhotoEditorMenu';
import {TransformMenu} from './TransformMenu/TransformMenu';

import './PhotoEditorBottomSheet.sass';

export const PhotoEditorBottomSheet = ({
  scrollToTop,
  setShowMenu,
  showMenu,
  uyoImage,
  activeDesignToolMenu: activeMenu,
  setActiveDesignToolMenu: setActiveMenu,
}) => {
  const {canvas, activeObjects, setActiveObjects} = useContext(EventCreateContext);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const countOfUyoImages = useSelector(selectCountOfUyoImages);
  const backgroundImage = useSelector(selectFabricArtBackgroundImage);
  const isMultipleUyo = countOfUyoImages > 1;

  const [activeTransform, setActiveTransform] = useState(TRANSFORM_TYPES.ROTATE);

  const {uploading, handleImageUpload} = useImageUpload(canvas);
  const {removeUYOImage} = useUyo();

  const handleClose = useCallback(() => {
    scrollToTop();
    setShowMenu(false);
    canvas.discardActiveObject();
    canvas.renderAll();
    setActiveMenu(MENU_TYPES.MAIN);
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {editingMobile: false},
    });
  }, [canvas, scrollToTop, setShowMenu, dispatch, setActiveMenu]);

  const imageUploadedCallback = useCallback(
    (uploadedUyo) => {
      const previousActiveObj = canvas.getActiveObject();
      canvas.remove(previousActiveObj);
      canvas.discardActiveObject();
      canvas.fire('object:removed');
      canvas.setActiveObject(uploadedUyo);
      canvas.renderAll();
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {activeCardEditor: 'image'},
      });
    },
    [canvas, dispatch]
  );

  const handleSwapPhotoUpload = async (event) => {
    const files = event.target ? event.target.files : [];
    const [file] = files;

    if (file) {
      try {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {activeCardEditor: 'image'},
        });

        const activeObj = canvas.getActiveObject();
        if (!activeObj || activeObj.type !== 'uYOImage') return;

        await handleImageUpload(file, {
          id: activeObj.id,
          callback: imageUploadedCallback,
          resetInputRef: fileInputRef,
          isMultipleUyo,
          backgroundImage,
        });
      } catch (_e) {
        // Error handling is done in the hook
      }
    }
  };

  const handleMenuItemClick = useCallback(
    (menuType) => {
      if (menuType === 'transform') {
        setActiveMenu(MENU_TYPES.TRANSFORM);
        setActiveTransform(TRANSFORM_TYPES.ROTATE);
      } else if (menuType === 'remove') {
        removeUYOImage(handleClose);
      } else if (menuType === 'filters') {
        if (!activeObjects) {
          const selectUyoImage = canvas.getObjects().find((obj) => obj.type === 'uYOImage');
          setActiveObjects(selectUyoImage);
          canvas.setActiveObject(selectUyoImage);
          canvas.renderAll();
          canvas.fire('object:modified');
        }
        setActiveMenu(MENU_TYPES.FILTERS);
      } else if (menuType === 'swap') {
        // Directly trigger file input click when swap is selected
        if (fileInputRef.current && !uploading) {
          fileInputRef.current.click();
        }
      }
    },
    [canvas, removeUYOImage, handleClose, uploading, activeObjects, setActiveMenu, setActiveObjects]
  );

  const renderMenuContent = () => {
    switch (activeMenu) {
      case MENU_TYPES.TRANSFORM:
        return (
          <TransformMenu
            activeTransform={activeTransform}
            setActiveTransform={setActiveTransform}
            canvas={canvas}
            uyoImage={uyoImage}
          />
        );
      case MENU_TYPES.FILTERS:
        return <ImageFilters />;
      default:
        return <MainPhotoEditorMenu onMenuItemClick={handleMenuItemClick} />;
    }
  };

  if (!showMenu) return null;

  return (
    <div className="photo-editor-bottom-sheet">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        onChange={handleSwapPhotoUpload}
        style={{display: 'none'}}
        aria-hidden="true"
      />
      <div className="photo-editor-bottom-sheet__content">{renderMenuContent()}</div>
    </div>
  );
};
